import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { AlertOctagon, Megaphone, FileText, GitBranch, Phone, FileQuestion } from 'lucide-react';
import type { StruggleIndicator, FeatureType } from '@respectly/shared-types';

interface StruggleIndicatorsPanelProps {
  data: StruggleIndicator[];
  isLoading?: boolean;
}

const FEATURE_CONFIG: Record<FeatureType, { icon: React.ComponentType<any>; color: string; label: string }> = {
  campaigns: { icon: Megaphone, color: '#3b82f6', label: 'Campaign' },
  documents: { icon: FileText, color: '#8b5cf6', label: 'Document' },
  forms: { icon: FileQuestion, color: '#10b981', label: 'Form' },
  voice: { icon: Phone, color: '#f59e0b', label: 'Voice' },
  workflows: { icon: GitBranch, color: '#a855f7', label: 'Workflow' },
  conversations: { icon: Megaphone, color: '#06b6d4', label: 'Messaging' },
};

export function StruggleIndicatorsPanel({ data, isLoading }: StruggleIndicatorsPanelProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
        <CardHeader className="pb-3">
          <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 flex items-center justify-center">
            <AlertOctagon className="h-4 w-4 text-orange-500" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Failure Hotspots
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">Where users are struggling most</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length === 0 ? (
          <div className="py-8 text-center">
            <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
              <AlertOctagon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">No failures recorded</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Everything is working smoothly.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((item, index) => {
              const fConf = FEATURE_CONFIG[item.feature];
              const FeatureIcon = fConf?.icon ?? Megaphone;
              const widthPct = Math.max((item.count / maxCount) * 100, 4);
              const rank = index + 1;

              return (
                <div key={`${item.feature}-${item.reason}-${index}`} className="group">
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold mt-0.5"
                      style={{ backgroundColor: `${fConf?.color}20`, color: fConf?.color }}
                    >
                      {rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold shrink-0"
                            style={{ color: fConf?.color, backgroundColor: `${fConf?.color}15` }}
                          >
                            <FeatureIcon className="h-2.5 w-2.5" />
                            {fConf?.label}
                          </span>
                          <span
                            className="text-xs text-slate-600 dark:text-slate-300 truncate"
                            title={item.reason}
                          >
                            {item.reason}
                          </span>
                        </div>
                        <span className="text-xs font-bold tabular-nums text-slate-700 dark:text-slate-300 shrink-0">
                          {item.count.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${widthPct}%`, backgroundColor: fConf?.color, opacity: 0.7 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
